// react
import React from 'react'

// hooks
import { useRetractSidebar } from '_hooks'

// services
import { get } from '_services/requests'
import { logErrorAsync } from '_lib/utility'
import { saveAs } from 'file-saver'
import { getExpiringReport } from './audience-creator/utils'

// components
import Header from '_components/header'
import Table from '_components/table'
import { IconButton } from '@st-operations/uikit/src/v2'
import downloadCSV from '../lib/downloadCSV'

const getDownload = (path) =>
  get(`downloads?file=${path}`, { removeTrailingSlash: true, transformPayload: false })

const downloadData = [
  {
    name: 'Expiring Audiences Report',
    filename: 'expiring-audiences.csv',
    downloadType: 'text/csv',
    path: 'custom-audiences',
    customHandler: () => {
      getExpiringReport()
    },
  },
  {
    name: 'Curated Data Feeds',
    filename: 'feeds.csv',
    downloadType: 'text/csv',
    path: 'event-feeds',
    customHandler: () => {
      fetch(`${window._api.backend}/event-feeds/feeds.csv`, {
        method: 'get',
        headers: {
          'content-type': 'text/csv;charset=UTF-8',
          Authorization: `Bearer ${window._api.token}`,
        },
      })
        .then((res) => res.text())
        .then((data) => downloadCSV(data, 'feeds'))
        .catch((error) => {
          logErrorAsync(error)
          toast.error(error.message)
        })
    },
  },
  {
    name: 'ShareThis Taxonomy IDs (Global/US)',
    filename: 'sharethis-taxonomy-ids.csv',
    downloadType: 'text/csv',
    path: 'sharethis-taxonomy-ids',
  },
  {
    name: 'ShareThis Taxonomy Counts (Global/US/AU/GB/CA/BR/MX)',
    filename: 'sharethis-taxonomy-counts.csv',
    downloadType: 'text/csv',
    path: 'sharethis-taxonomy-counts',
  },
  {
    name: 'Audience IDs',
    filename: 'audience-ids.csv',
    downloadType: 'text/csv',
    path: 'audience-ids',
  },
  {
    name: 'Taxonomy Delivery Report (White Label + Branded)',
    filename: 'taxonomy-delivery-report.csv',
    downloadType: 'text/csv',
    path: 'taxonomy-delivery-report',
  },
  {
    name: 'AppNexus Revenue (Month To Date)',
    filename: `appnexus-revenue-month-to-date-${new Date().toISOString().slice(0, 10)}.csv`,
    downloadType: 'text/csv',
    path: 'month-to-date-revenue',
  },
  {
    name: 'AppNexus Revenue (Last Month)',
    filename: `appnexus-revenue-last-month-${new Date().toISOString().slice(0, 10)}.csv`,
    downloadType: 'text/csv',
    path: 'last-month-revenue',
  },
  {
    name: 'AppNexus Revenue (Last 7 Days)',
    filename: `appnexus-revenue-last-7-days-${new Date().toISOString().slice(0, 10)}.csv`,
    downloadType: 'text/csv',
    path: 'last-seven-days-revenue',
  },
  {
    name: 'AppNexus Revenue (All Time)',
    filename: `appnexus-revenue-all-time-${new Date().toISOString().slice(0, 10)}.csv`,
    downloadType: 'text/csv',
    path: 'all-time-revenue',
  },
  {
    name: 'Appnexus IDs',
    filename: 'appnexus-ids.csv',
    downloadType: 'text/csv',
    path: 'appnexus-ids',
  },
  {
    name: 'Audience Sizes By Month',
    filename: 'sizes-by-month.csv',
    downloadType: 'text/csv',
    path: 'sizes-by-month',
  },
  {
    name: 'SDF Attributes',
    filename: 'sdfs.csv',
    downloadType: 'text/csv',
    path: 'sdfs',
  },
]

const Downloads = () => {
  useRetractSidebar()

  const onDownloadClick = (path, downloadType, filename) => {
    getDownload(path)
      .then((data) => {
        const { records } = data
        const blob = new Blob([records], { type: downloadType })
        saveAs(blob, filename)
        toast('Download successful!!')
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
  }

  const renderDownloadButton = (item) => {
    const { path, downloadType, filename, customHandler } = item

    const onDownload = () => {
      if (customHandler) {
        customHandler()
      } else {
        onDownloadClick(path, downloadType, filename)
      }
    }

    return (
      <IconButton
        text="Download"
        onClick={onDownload}
        iconProps={{ icon: 'download', type: 'outline', width: 24, height: 24 }}
      />
    )
  }

  const tableColumns = [
    {
      key: 'name',
      name: 'File',
      sortable: false,
    },
    {
      name: 'Download',
      key: 'file',
      render: (item) => renderDownloadButton(item),
      styles: {
        maxWidth: '300px',
        textAlign: 'center',
      },
      className: 'center',
    },
  ]

  return (
    <>
      <Header title="Downloads" />
      <Table columns={tableColumns} data={downloadData} />
    </>
  )
}

export default Downloads
